import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import {
  Box,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS';
import CodeIcon from '@mui/icons-material/Code';
export const WHATSAPP_BODY_MAX_LENGTH = 1024;

export type WhatsAppBodyEditorHandle = {
  getSelectionStart: () => number;
  setSelectionRange: (start: number, end: number) => void;
  focus: () => void;
};

type FormatKind = 'bold' | 'italic' | 'strike' | 'code';

const FORMAT_CONFIG: Record<
  FormatKind,
  { label: string; prefix: string; suffix: string }
> = {
  bold: { label: 'Bold (*text*)', prefix: '*', suffix: '*' },
  italic: { label: 'Italic (_text_)', prefix: '_', suffix: '_' },
  strike: { label: 'Strikethrough (~text~)', prefix: '~', suffix: '~' },
  code: { label: 'Monospace (```text```)', prefix: '```', suffix: '```' },
};

function applyWrap(
  text: string,
  start: number,
  end: number,
  prefix: string,
  suffix: string
) {
  const selected = text.slice(start, end);
  const wrapped = `${prefix}${selected}${suffix}`;
  const newText = text.slice(0, start) + wrapped + text.slice(end);
  const cursorStart = start + prefix.length;
  const cursorEnd = cursorStart + selected.length;
  return { newText, cursorStart, cursorEnd };
}

type WhatsAppBodyEditorProps = {
  value: string;
  onChange: (text: string) => void;
  error?: boolean;
  helperText?: React.ReactNode;
  placeholder?: string;
  maxLength?: number;
};

const WhatsAppBodyEditor = forwardRef<WhatsAppBodyEditorHandle, WhatsAppBodyEditorProps>(
  (
    {
      value,
      onChange,
      error = false,
      helperText,
      placeholder = 'Write your message body…',
      maxLength = WHATSAPP_BODY_MAX_LENGTH,
    },
    ref
  ) => {
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);

    useImperativeHandle(ref, () => ({
      getSelectionStart: () => textareaRef.current?.selectionStart ?? value.length,
      setSelectionRange: (start, end) => {
        textareaRef.current?.setSelectionRange(start, end);
        textareaRef.current?.focus();
      },
      focus: () => textareaRef.current?.focus(),
    }));

    const applyFormat = (kind: FormatKind) => {
      const el = textareaRef.current;
      if (!el) return;

      const start = el.selectionStart ?? 0;
      const end = el.selectionEnd ?? 0;
      const { prefix, suffix } = FORMAT_CONFIG[kind];
      const { newText, cursorStart, cursorEnd } = applyWrap(value, start, end, prefix, suffix);

      if (newText.length > maxLength) {
        return;
      }

      onChange(newText);
      setTimeout(() => {
        el.focus();
        el.setSelectionRange(cursorStart, cursorEnd);
      }, 0);
    };

    return (
      <Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: 1,
            py: 0.5,
            border: '1px solid',
            borderColor: error ? 'error.main' : 'divider',
            borderBottom: 'none',
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
            bgcolor: 'grey.50',
          }}
        >
          <Tooltip title={FORMAT_CONFIG.bold.label}>
            <IconButton size="small" onClick={() => applyFormat('bold')} aria-label="Bold">
              <FormatBoldIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={FORMAT_CONFIG.italic.label}>
            <IconButton size="small" onClick={() => applyFormat('italic')} aria-label="Italic">
              <FormatItalicIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={FORMAT_CONFIG.strike.label}>
            <IconButton
              size="small"
              onClick={() => applyFormat('strike')}
              aria-label="Strikethrough"
            >
              <StrikethroughSIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={FORMAT_CONFIG.code.label}>
            <IconButton size="small" onClick={() => applyFormat('code')} aria-label="Monospace">
              <CodeIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
            WhatsApp: *bold* _italic_ ~strike~ ```code```
          </Typography>
        </Box>

        <TextField
          inputRef={textareaRef}
          multiline
          fullWidth
          rows={3}
          placeholder={placeholder}
          value={value}
          onChange={(e) => {
            const next = e.target.value;
            if (next.length <= maxLength) {
              onChange(next);
            }
          }}
          error={error}
          inputProps={{ maxLength }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0,
              alignItems: 'flex-start',
            },
            '& .MuiInputBase-inputMultiline': {
              resize: 'vertical',
              overflow: 'hidden !important',
              minHeight: '60px',
              boxSizing: 'border-box',
            },
          }}
        />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
          <Typography variant="caption" color={error ? 'error' : 'text.secondary'} component="div">
            {helperText}
          </Typography>
          <Typography
            variant="caption"
            color={(value?.length || 0) > maxLength ? 'error' : 'text.secondary'}
          >
            {value?.length || 0}/{maxLength} characters
          </Typography>
        </Box>
      </Box>
    );
  }
);

WhatsAppBodyEditor.displayName = 'WhatsAppBodyEditor';

export default WhatsAppBodyEditor;
